import React, {useEffect, useState} from "react";
import PlotlyPlot from "../../../../common/components/PlotlyPlot";
import {loadProtocolDeltaHRData} from "../../services/protocols";
import {PlotParams} from "react-plotly.js";
import {IProtocol} from "../../../types/protocol";
import {ICompany} from "../../../../common/types";
import {BiomarkerNames} from "../../types/biomarkerNames";
import {Affix, Box, Loader, Text, Select, NumberInput} from "@mantine/core";

interface DeltaHRChartProps {
    activeCompany: ICompany;
    selectedProtocol: IProtocol | undefined;
    refreshTrigger?: number
}

export const DeltaHRChart: React.FC<DeltaHRChartProps> = ({activeCompany, selectedProtocol, refreshTrigger}) => {
    const [deltaHRData, setDeltaHRData] = useState<PlotParams>()
    const [loading, setLoading] = useState<boolean>(false)

    // acceptable increase and decrease values (defaults per request)
    const [acceptableIncrease, setAcceptableIncrease] = useState<number>(3)
    const [acceptableDecrease, setAcceptableDecrease] = useState<number>(-3)

    useEffect(() => {
        if (selectedProtocol) {
            setLoading(true)
            loadProtocolDeltaHRData(activeCompany.id, selectedProtocol, BiomarkerNames.HR, ["pre_to_post_delta"]).then((data: any) => {
                // data.data expected to be array of [stim_condition, delta_hr]
                // build mapping
                const conn_map = new Map<string, number>();
                const stimSet = new Set<string>();
                (data.data || []).forEach(([stim, delta_hr]: [string, number]) => {
                    stimSet.add(stim);
                    conn_map.set(stim, delta_hr);
                });

                // Preserve order as sorted by stim_start_time in query
                const stimOrder = Array.from(stimSet);

                const lineTemplate = (data.figure?.data && (data.figure.data[0] as any)) || {} as any;
                const templateMarker = (lineTemplate && lineTemplate.marker) || {};
                const baseColor = templateMarker.color ?? templateMarker.fillcolor ?? "rgba(31,119,180,0.8)";

                const HRTrace: any = {
                    ...lineTemplate,
                    x: stimOrder,
                    y: stimOrder.map((st) => conn_map.get(st) || 0),
                    marker: {
                        ...templateMarker,
                        color: baseColor,
                    },
                    mode: lineTemplate.mode || 'lines+markers',
                    hovertemplate: "%{x}: %{y}<extra></extra>",
                };

                // Build threshold shapes and mark them so we can remove/replace later (_is_threshold)
                const rectShape = {
                    type: 'rect',
                    xref: 'paper',
                    x0: 0,
                    x1: 1,
                    yref: 'y',
                    y0: acceptableDecrease,
                    y1: acceptableIncrease,
                    fillcolor: 'rgba(255,0,0,0.08)',
                    line: { width: 0 },
                    _is_threshold: true,
                } as any;

                const topLine = {
                    type: 'line',
                    xref: 'paper',
                    x0: 0,
                    x1: 1,
                    yref: 'y',
                    y0: acceptableIncrease,
                    y1: acceptableIncrease,
                    line: { color: 'rgba(255,0,0,1)', width: 1, dash: 'dot' },
                    _is_threshold: true,
                } as any;

                const bottomLine = {
                    type: 'line',
                    xref: 'paper',
                    x0: 0,
                    x1: 1,
                    yref: 'y',
                    y0: acceptableDecrease,
                    y1: acceptableDecrease,
                    line: { color: 'rgba(255,0,0,1)', width: 1, dash: 'dot' },
                    _is_threshold: true,
                } as any;

                const existingShapes = (data.figure.layout?.shapes || []) as any[];
                // keep shapes that are not threshold shapes (if any came from server)
                const nonThresholdShapes = existingShapes.filter((s: any) => !s._is_threshold);

                const layoutWithShapes = {
                    ...data.figure.layout,
                    shapes: [rectShape, topLine, bottomLine, ...nonThresholdShapes]
                };

                setDeltaHRData({
                    layout: layoutWithShapes,
                    config: { responsive: true },
                    data: [HRTrace],
                })
            }).catch(() => {
                // eat the error since notification would already be called
            }).finally(() => {
                setLoading(false)
            })
        }
    }, [selectedProtocol, refreshTrigger])

    // When thresholds change, replace previous threshold shapes (those marked with _is_threshold)
    useEffect(() => {
        if (!deltaHRData) return

        const rectShape = {
            type: 'rect',
            xref: 'paper',
            x0: 0,
            x1: 1,
            yref: 'y',
            y0: acceptableDecrease,
            y1: acceptableIncrease,
            fillcolor: 'rgba(255,0,0,0.08)',
            line: { width: 0 },
            _is_threshold: true,
        } as any;

        const topLine = {
            type: 'line',
            xref: 'paper',
            x0: 0,
            x1: 1,
            yref: 'y',
            y0: acceptableIncrease,
            y1: acceptableIncrease,
            line: { color: 'rgba(255,0,0,1)', width: 1, dash: 'dot' },
            _is_threshold: true,
        } as any;

        const bottomLine = {
            type: 'line',
            xref: 'paper',
            x0: 0,
            x1: 1,
            yref: 'y',
            y0: acceptableDecrease,
            y1: acceptableDecrease,
            line: { color: 'rgba(255,0,0,1)', width: 1, dash: 'dot' },
            _is_threshold: true,
        } as any;

        const existingShapes = ((deltaHRData.layout as any)?.shapes || []) as any[];
        const nonThresholdShapes = existingShapes.filter((s: any) => !s._is_threshold);

        const newLayout = {
            ...(deltaHRData.layout || {}),
            shapes: [rectShape, topLine, bottomLine, ...nonThresholdShapes]
        };

        setDeltaHRData({
            ...(deltaHRData as any),
            layout: newLayout
        })
    }, [acceptableIncrease, acceptableDecrease])

    const renderLoading = () => {
        return (
            <Box style={{position: "absolute", zIndex: "10", top: "calc(50% - 2em)", left: "calc(50% - 2em)"}}>
                <Loader size="4em" color="var(--bios-strong-accent)"/>
            </Box>
        )
    }

    return (
        <Box style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <Box style={{flex: '0 0 auto', display: 'flex', gap: '1rem', padding: '0.5rem', alignItems: 'center'}}>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Acceptable increase</Text>
                    <NumberInput
                        value={acceptableIncrease}
                        onChange={(v: number | undefined) => setAcceptableIncrease(typeof v === 'number' ? v : 0)}
                        step={0.5}
                        precision={1}
                        min={0}
                        styles={{ input: { width: 140 } }}
                    />
                </Box>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Acceptable decrease</Text>
                    <NumberInput
                        value={acceptableDecrease}
                        onChange={(v: number | undefined) => setAcceptableDecrease(typeof v === 'number' ? v : 0)}
                        step={0.5}
                        precision={1}
                        max={0}
                        styles={{ input: { width: 140 } }}
                    />
                </Box>
            </Box>

            <Box style={{flex: '1 1 auto', minHeight: 0, position: 'relative', overflow: 'hidden'}}>
                {deltaHRData ? (
                    <Box style={{height: '100%', width: '100%'}}>
                        <PlotlyPlot {...deltaHRData}/>
                        {loading ? renderLoading() : null}
                    </Box>
                ) : (
                    <Box style={{height: '100%', position: 'relative'}}>
                        {renderLoading()}
                    </Box>
                )}
            </Box>
        </Box>
    )
}
